/**
 * Browser detection utilities for handling Safari third-party cookie restrictions
 */

/**
 * Detects if the current browser is Safari or uses WebKit engine
 */
export function isSafari(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  // Check for Safari and exclude Chrome/Chromium which also contain 'safari' in UA
  return (
    ua.includes('safari') &&
    !ua.includes('chrome') &&
    !ua.includes('chromium') &&
    !ua.includes('android')
  );
}

/**
 * Detects if the current browser is running on iOS (iPhone, iPad, iPod)
 */
export function isIOS(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua);
}

/**
 * Detects if the application is running inside an iframe
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we get a SecurityError, we're definitely in an iframe from a different origin
    return true;
  }
}

/**
 * Detects if the application is running in a third-party context (iframe on different domain)
 */
export function isThirdPartyContext(): boolean {
  if (!isInIframe()) {
    return false;
  }

  try {
    // Try to access parent origin - if we can, we're same-origin
    const parentOrigin = window.parent.location.origin;
    return parentOrigin !== window.location.origin;
  } catch (e) {
    // SecurityError means we're cross-origin (third-party)
    return true;
  }
}

/**
 * Detects if the browser is likely to block third-party cookies.
 * All modern browsers block third-party cookies in iframe contexts:
 * - Safari/iOS: always blocks
 * - Chrome 115+: blocks (Privacy Sandbox / third-party cookie deprecation)
 * - Firefox: blocks with Enhanced Tracking Protection
 *
 * Safest approach: if we're in a third-party iframe, assume cookies may be blocked.
 */
export function mayBlockThirdPartyCookies(): boolean {
  if (isThirdPartyContext()) {
    return true;
  }
  return isSafari() || isIOS();
}

/**
 * Checks if Storage Access API is supported by the browser
 */
export function isStorageAccessAPISupported(): boolean {
  return (
    typeof document !== 'undefined' &&
    'hasStorageAccess' in document &&
    'requestStorageAccess' in document
  );
}

/**
 * Comprehensive check if we need to handle storage access
 * Returns true if:
 * - Running in iframe on different domain
 * - Browser may block third-party cookies
 * - Storage Access API is available
 */
export function needsStorageAccess(): boolean {
  return (
    isThirdPartyContext() &&
    mayBlockThirdPartyCookies() &&
    isStorageAccessAPISupported()
  );
}
